import { useState } from "react"
import { router } from "@inertiajs/react"
import { BrainCircuit, ChevronDown, ChevronUp, Loader2, CheckSquare, Square } from "lucide-react"

type ProposalType = "update_field" | "create_credit" | "add_to_notes" | "create_chart_listing"

type Proposal = {
  id: string
  type: ProposalType
  description: string
  // update_field
  field?: string
  value?: unknown
  // create_credit
  person_name?: string
  person_id?: number | null
  person_slug?: string | null
  role?: string
  details?: string | null
  // add_to_notes
  note_text?: string
  // create_chart_listing
  position?: number
  chart_date?: string | null
}

type Props = {
  entityType: "Band" | "Person" | "Recording" | "Venue"
  entityId: number
}

function getCsrfToken(): string {
  return document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? ""
}

export function ResearchAssistantPanel({ entityType, entityId }: Props) {
  const [open, setOpen]               = useState(false)
  const [text, setText]               = useState("")
  const [loading, setLoading]         = useState(false)
  const [answer, setAnswer]           = useState<string | null>(null)
  const [proposals, setProposals]     = useState<Proposal[] | null>(null)
  const [selected, setSelected]       = useState<Set<string>>(new Set())
  const [confirming, setConfirming]   = useState(false)
  const [error, setError]             = useState<string | null>(null)

  function toggleProposal(id: string) {
    setSelected((prev) => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  async function handleAnalyse() {
    if (!text.trim()) return
    setLoading(true)
    setProposals(null)
    setAnswer(null)
    setSelected(new Set())
    setError(null)

    try {
      const res = await fetch("/admin/research_assistant/analyze", {
        method:  "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": getCsrfToken() },
        body:    JSON.stringify({ entity_type: entityType, entity_id: entityId, text }),
      })
      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        throw new Error(body.error || `Server error ${res.status}`)
      }
      const data = await res.json()
      const fetched: Proposal[] = data.proposals ?? []
      setAnswer(data.answer ?? null)
      setProposals(fetched)
      setSelected(new Set(fetched.map((p) => p.id)))
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not analyse text. Please try again.")
    } finally {
      setLoading(false)
    }
  }

  function handleConfirm() {
    if (!proposals) return
    const accepted = proposals.filter((p) => selected.has(p.id))
    if (accepted.length === 0) return
    setConfirming(true)

    router.post(
      "/admin/research_assistant/confirm",
      { entity_type: entityType, entity_id: entityId, proposals: JSON.stringify(accepted) },
      {
        onFinish: () => {
          setConfirming(false)
          setProposals(null)
          setAnswer(null)
          setSelected(new Set())
          setText("")
        },
      }
    )
  }

  function reset() {
    setProposals(null)
    setAnswer(null)
    setSelected(new Set())
    setError(null)
    setText("")
  }

  const selectedCount = selected.size

  return (
    <div className="mt-10 border border-dashed border-amber-300 dark:border-amber-800 rounded-xl overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2.5 px-4 py-3 bg-amber-50 dark:bg-amber-950/30 hover:bg-amber-100 dark:hover:bg-amber-950/50 transition-colors text-left"
      >
        <BrainCircuit className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0" />
        <span className="text-sm font-medium text-amber-800 dark:text-amber-300 flex-1">Research Assistant</span>
        {open
          ? <ChevronUp className="w-4 h-4 text-amber-500 shrink-0" />
          : <ChevronDown className="w-4 h-4 text-amber-500 shrink-0" />}
      </button>

      {open && (
        <div className="p-4 bg-white dark:bg-stone-950 space-y-4">
          {!proposals && (
            <>
              <p className="text-xs text-stone-500 dark:text-stone-400 leading-relaxed">
                Paste research text below — a Wikipedia excerpt, discography notes, a conversation with Claude, anything. The assistant will identify what's new and propose specific database changes for your review.
              </p>
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={6}
                placeholder="Paste research text here…"
                className="w-full text-sm rounded-lg border border-stone-200 dark:border-stone-700 bg-stone-50 dark:bg-stone-900 text-stone-900 dark:text-white placeholder:text-stone-400 dark:placeholder:text-stone-600 px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-amber-400 resize-y"
              />
              {error && <p className="text-xs text-red-600 dark:text-red-400">{error}</p>}
              <button
                type="button"
                onClick={handleAnalyse}
                disabled={loading || !text.trim()}
                className="flex items-center gap-2 px-3.5 py-1.5 text-sm font-medium bg-amber-500 hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
              >
                {loading && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                {loading ? "Analysing…" : "Analyse"}
              </button>
            </>
          )}

          {proposals && (
            <>
              {answer && (
              <div className="rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 px-3.5 py-3">
                <p className="text-xs font-medium text-amber-700 dark:text-amber-400 mb-1">Answer</p>
                <p className="text-sm text-stone-800 dark:text-stone-200 leading-relaxed whitespace-pre-wrap">{answer}</p>
              </div>
            )}

            {proposals.length === 0 ? (
                <div className="text-sm text-stone-500 dark:text-stone-400 py-2">
                  {answer ? "No database changes to propose based on this answer." : "No new information found in the provided text."}
                </div>
              ) : (
                <>
                  <p className="text-xs text-stone-500 dark:text-stone-400">
                    {proposals.length} proposal{proposals.length !== 1 ? "s" : ""} found. Review and confirm what to save:
                  </p>
                  <div className="divide-y divide-stone-100 dark:divide-stone-800">
                    {proposals.map((p) => {
                      const isSelected = selected.has(p.id)
                      return (
                        <button
                          key={p.id}
                          type="button"
                          onClick={() => toggleProposal(p.id)}
                          className="w-full flex items-start gap-3 py-3 text-left hover:bg-stone-50 dark:hover:bg-stone-900/60 -mx-4 px-4 transition-colors"
                        >
                          {isSelected
                            ? <CheckSquare className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                            : <Square className="w-4 h-4 text-stone-300 dark:text-stone-600 shrink-0 mt-0.5" />}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-stone-800 dark:text-stone-200 leading-snug">{p.description}</p>
                            <span className="inline-block mt-1 text-[11px] font-mono text-stone-400 dark:text-stone-500 bg-stone-100 dark:bg-stone-800 px-1.5 py-px rounded">
                              {p.type}
                            </span>
                            {p.type === "create_credit" && !p.person_id && (
                              <span className="ml-1.5 text-[11px] text-amber-600 dark:text-amber-400">· will create new person</span>
                            )}
                          </div>
                        </button>
                      )
                    })}
                  </div>

                  <div className="flex items-center gap-3 pt-1">
                    <button
                      type="button"
                      onClick={handleConfirm}
                      disabled={confirming || selectedCount === 0}
                      className="flex items-center gap-2 px-3.5 py-1.5 text-sm font-medium bg-amber-500 hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
                    >
                      {confirming && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                      {confirming ? "Saving…" : `Save ${selectedCount} change${selectedCount !== 1 ? "s" : ""}`}
                    </button>
                    <button
                      type="button"
                      onClick={reset}
                      className="text-sm text-stone-400 hover:text-stone-600 dark:hover:text-stone-300 transition-colors"
                    >
                      Start over
                    </button>
                  </div>
                </>
              )}

              {proposals.length === 0 && (
                <button type="button" onClick={reset} className="text-sm text-amber-600 dark:text-amber-400 hover:underline">
                  Try different text
                </button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
}
